import {
    IonPage,
    IonContent,
    IonicSlides,
    IonTitle,
    IonChip,
    IonIcon,
    IonLabel,
    IonModal,
    IonButton,
    ToastOptions,
    useIonToast,
} from '@ionic/react';
import { alertCircleOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { each, filter, map, size } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { EffectCreative, Pagination, Keyboard } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import DayPicker from '../components/DayPicker';
import { Header } from '../components/Header';
import { IosHeader } from '../components/IosHeader';
import PointingDetail, { PointingDetailHandle, ViewMode } from '../components/common/PointingDetail';
import { useEmployees, usePointing } from '../hooks/useSwr';
import { Pointing, PointingToSaved } from '../types/models';
import { defaultToastOptions } from '../utils/Global';
import { saveOrCreateWithoutRead, unstandardizedOdooPointing } from '../utils/odoo';
import { cleanDataBeforeSending, getMainPointings, getMetaData, PointingState } from '../utils/odooUtils';
import History, { getFieldsChanged, getLastPointingByState } from './History';

export const MyPointing: React.FC = () => {
    // this page shows the pointings of the current user for the selected day
    const title = 'Mes Pointages';

    const { ids: employeeIds } = useEmployees();

    const [selectedDate, setSelectedDate] = useState(DateTime.now().startOf('day'));
    const [presentToast] = useIonToast();

    const { standardized: standardizedPointings, mutate } = usePointing(employeeIds, [
        ['date', '=', selectedDate.toISODate()],
        ['state', 'in', [PointingState.WaitValidation, PointingState.AdherentValidation, PointingState.GeiqValidation]],
    ]);
    const mainPointings = getMainPointings(standardizedPointings);

    const swiperRef = useRef<SwiperRef>(null);

    useEffect(() => {
        swiperRef.current?.swiper?.slideTo(0);
    }, [selectedDate]);

    return (
        <IonPage id="pointages-page">
            <IosHeader title={title} />
            <IonContent fullscreen={true}>
                <Header title={title} />
                <DayPicker
                    date={selectedDate}
                    onChange={(date) => {
                        setSelectedDate(date);
                    }}
                    employees={employeeIds}
                />
                <Swiper
                    ref={swiperRef}
                    slidesPerView={1}
                    spaceBetween={30}
                    grabCursor={true}
                    pagination={{
                        clickable: true,
                        dynamicBullets: true,
                    }}
                    keyboard={{
                        pageUpDown: true,
                    }}
                    modules={[EffectCreative, Pagination, Keyboard, IonicSlides]}
                >
                    {mainPointings.length ? (
                        map((pointing) => {
                            return (
                                <SwiperSlide key={`${pointing.date}_${pointing.id}`}>
                                    <MyPointingChild
                                        pointingValue={pointing}
                                        swiperRef={swiperRef.current}
                                        pointingsHistory={filter({ origin_id: pointing.id }, standardizedPointings)}
                                        onValidated={async () => {
                                            await mutate();
                                            presentToast({
                                                ...defaultToastOptions,
                                                message: `Le pointage a bien été modifié`,
                                            });
                                        }}
                                    />
                                </SwiperSlide>
                            );
                        }, mainPointings)
                    ) : (
                        <div className="grid h-[50vh] place-items-center">
                            <IonTitle className="text-center text-gray-500">Aucun pointage</IonTitle>
                        </div>
                    )}
                </Swiper>
            </IonContent>
        </IonPage>
    );
};

export const MyPointingChild: React.FC<{
    pointingValue: Pointing;
    pointingsHistory: Pointing[];
    swiperRef: SwiperRef | null;
    onValidated: () => void;
}> = ({ pointingValue, pointingsHistory, swiperRef, onValidated }) => {
    const pointingWasEdited =
        size(
            getFieldsChanged(
                getLastPointingByState(PointingState.WaitValidation, pointingsHistory, pointingValue.id),
                pointingValue
            )
        ) > 0;

    const [isEdited, setIsEdited] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [presentToast] = useIonToast();

    const modal = useRef<HTMLIonModalElement>(null);
    const refPointingDetail = useRef<PointingDetailHandle | null>(null);
    const onConfirm = async () => {
        if (!refPointingDetail.current) {
            return;
        }

        try {
            const pointing = refPointingDetail.current.getPointing();

            const extraData = await getMetaData();

            await saveOrCreateWithoutRead<PointingToSaved>(
                'hr.madper.app.pointing.time',
                unstandardizedOdooPointing({
                    ...cleanDataBeforeSending(pointing),
                    metadata: { ...pointing.metadata, confirm: { ...pointing.metadata.start, ...extraData } },
                }),
                { forwardIp: true, pointingState: 'confirm' }
            );

            setIsEdited(false);
            await onValidated();
        } catch (e: any) {
            each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
        }
    };

    const openHistoryModal = () => {
        swiperRef?.swiper?.keyboard?.disable();
        modal.current?.present();
    };

    const closeHistoryModal = () => {
        swiperRef?.swiper?.keyboard?.enable();
        modal.current?.dismiss();
    };

    return (
        <>
            <div className="absolute right-5 top-0 mt-1 z-20 text-red-500">
                <div className="flex items-center h-8">
                    {(pointingValue.state === PointingState.AdherentValidation ||
                        pointingValue.state === PointingState.GeiqValidation) && (
                        <>
                            <IonChip color={pointingWasEdited ? 'warning' : 'success'} onClick={openHistoryModal}>
                                <IonIcon icon={pointingWasEdited ? alertCircleOutline : checkmarkCircleOutline} />
                                <IonLabel>
                                    {pointingValue.state === PointingState.AdherentValidation ? 'Adhérent' : 'GEIQ'}
                                </IonLabel>
                            </IonChip>
                            <IonModal ref={modal} className="fullscreen-modal">
                                <History
                                    pointingId={pointingValue.id}
                                    dismiss={closeHistoryModal}
                                    pointingsHistory={pointingsHistory}
                                    viewMode={ViewMode.Confirm}
                                />
                            </IonModal>
                        </>
                    )}
                </div>
            </div>
            <div className="mt-4">
                <PointingDetail
                    initialPointing={pointingValue}
                    viewMode={ViewMode.Confirm}
                    isDisabled={!pointingValue._access_right.write}
                    onChange={(pointing: Pointing) => {
                        if (!isEdited) {
                            setIsEdited(true);
                        }
                        setIsValid(
                            !!(
                                (pointing.mad_id >= 0 || (pointing.mad_id === 0 && pointing.other_mad)) &&
                                (pointing.workplace_id >= 0 ||
                                    (pointing.workplace_id === 0 && pointing.other_workplace))
                            )
                        );
                    }}
                    ref={refPointingDetail}
                />
            </div>
            {pointingValue._access_right.write && (
                <div className="madper-button">
                    <IonButton
                        shape="round"
                        type="submit"
                        disabled={!isEdited || !isValid}
                        expand="block"
                        onClick={onConfirm}
                        className="btn"
                    >
                        {pointingValue.id ? 'Sauvegarder' : 'Créer'}
                    </IonButton>
                </div>
            )}
        </>
    );
};
